import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { UpgradeModule } from '@angular/upgrade/static';
import { inject, Injectable } from '@angular/core';
import { of, firstValueFrom, map, Observable, switchMap, from } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from './environments/environment';
import { SentinelService } from '@bannerflow/sentinel';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class LocalAuthService {
    private sentinel = inject(SentinelService);

    private cachedToken: string | null = null;
    private expiresAt = 0;

    constructor(private auth0Service: AuthService) {}

    private async refreshToken(): Promise<string | null> {
        const token = await firstValueFrom(this.auth0Service.getAccessTokenSilently());
        const claims = JSON.parse(atob(token!.split('.')[1]));
        this.expiresAt = claims.exp * 1000;
        this.cachedToken = token;
        return token;
    }

    private async getFreshToken(): Promise<string | null> {
        const now = Date.now();
        if (this.cachedToken && now < this.expiresAt - 10_000) {
            return this.cachedToken;
        }
        return this.refreshToken();
    }

    getToken(): Observable<string | null> {
        return from(this.getFreshToken());
    }

    getTokenAsync(): Promise<string | null> {
        return this.getFreshToken();
    }
}

interface CurrentAccountBrandResult {
    accountSlug: string;
    brandSlug: string;
}

interface User {
    isEmployee: boolean;
    id: string;
    externalId: string;
    firstName: string;
    lastName: string;
    email: string;
    employee: number;
    created: Date;
    role: number;
    defaultBrand: string;
    title?: string;
    currentAccount?: Account;
    currentBrand?: Brand;
    accounts?: Account[];
}

interface TrackingResult {
    user: User;
    accounts: Account[];
}

interface Account {
    slug?: string;
    brands: Brand[];
}

interface Brand {
    slug: string;
    logo: string;
    current?: boolean;
}

interface BrandPicker {
    Id: string;
    accountSlug: string;
    name: string;
    logo: string;
    slug: string;
    state: string;
}

bootstrapApplication(AppComponent, appConfig)
    .then(appRef => {
        let bootstrap = false;
        const upgrade = appRef.injector.get(UpgradeModule);
        const authService = appRef.injector.get(LocalAuthService);
        const http = appRef.injector.get(HttpClient);
        const sentinel = appRef.injector.get(SentinelService);

        Promise.all([
            import('./app/core/unleash/feature-flag.service'),
            import('./app/core/unleash/feature-flag-debug')
        ]).then(([serviceModule, debugModule]) => {
            const featureFlagService = appRef.injector.get(serviceModule.FeatureFlagService);
            (window as any)['unleashClientInstance'] = featureFlagService;
            debugModule.setupFeatureFlagDebug(environment.stage === 'production');
        });

        authService
            .getToken()
            .pipe(
                switchMap(token => {
                    if (!token) {
                        return of(null);
                    }

                    (window as any)['getAuthToken'] = async (): Promise<string | null> =>
                        await authService.getTokenAsync();

                    (window as any)['token'] = token;
                    (window as any)['BANNERFLOW_LEGACY_URL'] = environment.LEGACY_URL;
                    (window as any)['LANDING_PAGE_URL'] = environment.LPB_URL;
                    (window as any)['HOME_URL'] = environment.HOME_URL;

                    const pathname = window.location.pathname;
                    const segments = pathname.split('/').filter(segment => segment);

                    if (segments.length > 0 && segments[0] === 'v2') {
                        window.open(
                            environment.HOME_URL + window.location.pathname.replace('/v2', ''),
                            '_self'
                        );
                        return of(null);
                    }

                    const accountSlugUrl = segments.length > 0 ? segments[0] : null;
                    const brandSlugUrl = segments.length > 1 ? segments[1] : null;

                    if (bootstrap) {
                        return of(null);
                    }
                    bootstrap = true;

                    return http
                        .get<CurrentAccountBrandResult>(`${environment.AA_URL}/api/user/defaults`, {
                            headers: { Authorization: `Bearer ${token}` }
                        })
                        .pipe(
                            switchMap(data => {
                                const accountSlug = accountSlugUrl ?? data.accountSlug;
                                const brandSlug = brandSlugUrl ?? data.brandSlug;

                                return http
                                    .get<TrackingResult>(
                                        `${environment.AA_URL}/account/${accountSlug}/tracking`,
                                        { headers: { Authorization: `Bearer ${token}` } }
                                    )
                                    .pipe(
                                        switchMap(info =>
                                            http
                                                .get<
                                                    BrandPicker[]
                                                >(`${environment.AA_URL}/api/${accountSlug}/${brandSlug}/users/current/brands/simple`, { headers: { Authorization: `Bearer ${token}` } })
                                                .pipe(
                                                    map(brandResult => {
                                                        for (const account of info.accounts) {
                                                            for (const brand of account.brands) {
                                                                const pickerBrand = brandResult.find(
                                                                    b => b.slug === brand.slug
                                                                );
                                                                if (pickerBrand) {
                                                                    brand.logo = pickerBrand.logo;
                                                                }
                                                            }
                                                        }

                                                        const currentAccount = info.accounts.find(
                                                            account => account.slug === accountSlug
                                                        );
                                                        const currentBrand =
                                                            currentAccount?.brands.find(
                                                                brand => brand.slug === brandSlug
                                                            );

                                                        if (currentBrand) {
                                                            currentBrand.current = true;
                                                        }

                                                        info.user.accounts = info.accounts;
                                                        info.user.currentAccount = currentAccount;
                                                        info.user.currentBrand = currentBrand;
                                                        info.user.isEmployee = info.user.employee === 3;

                                                        return info;
                                                    })
                                                )
                                        )
                                    );
                            })
                        );
                })
            )
            .subscribe(info => {
                if (!info) {
                    return;
                }

                (window as any)['BF_INFO'] = info;
                (window as any)['bannerFlowUser'] = info;
                sentinel.setUser(info.user.externalId, info.accounts[0].slug || '');

                if ((window as any)['unleashClientInstance']) {
                    const featureFlagService = (window as any)['unleashClientInstance'];
                    featureFlagService.setContextField('userId', info.user.externalId);
                    featureFlagService.setContextField('accountSlug', info.accounts[0].slug || '');
                }

                upgrade.bootstrap(document.body, ['BannerFlowApp'], {
                    strictDi: false
                });
            });
    })
    .catch(_ => {});
